import { Button, Checkbox, Dialog, Select, TextInput, Typography, toast } from '@neo4j-ndl/react';
import { ExclamationTriangleIconSolid } from '@neo4j-ndl/react/icons';
import { fleetManagementApi, useActiveProjectSummary } from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { useState } from 'react';

import { ApiErrorBanner } from '../../shared/components';
import { useTokenExpiryTimeOptions } from '../hooks/use-token-expiry-time-options';
import { TokenOutput } from '../shared/components/token-output';
import type { TokenExpiryOption } from '../shared/types';

export const TokenExpirySelection = ({
  expiry,
  setSelectedExpiry,
}: {
  setSelectedExpiry: (expiry: TokenExpiryOption) => void;
  expiry: TokenExpiryOption | undefined;
}) => {
  const expiryTimeOptions = useTokenExpiryTimeOptions();
  return (
    <Select
      label={<Typography variant="body-medium">Expiry</Typography>}
      type="select"
      size="medium"
      selectProps={{
        onChange: (option) => {
          if (option !== null) {
            setSelectedExpiry(option);
          }
        },
        isMulti: false,
        value: expiry,
        options: expiryTimeOptions,
        menuPosition: 'fixed',
      }}
    />
  );
};

export const TokenAutoRotationOption = ({
  autoRotate,
  setAutoRotate,
}: {
  autoRotate: boolean;
  setAutoRotate: (autoRotate: boolean) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Checkbox label="Auto-rotate token" isChecked={autoRotate} onChange={(e) => setAutoRotate(e.target.checked)} />
      <Typography variant="body-small" className="ndl-form-message">
        The token will be automatically rotated after it expires. This ensures that a valid token is always available
        for the plugin to report.
      </Typography>
    </div>
  );
};

export const TokenReleaseOption = ({
  release,
  setRelease,
}: {
  release: boolean;
  setRelease: (setRelease: boolean) => void;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Checkbox label="Release deployment" isChecked={release} onChange={(e) => setRelease(e.target.checked)} />
      </div>
      <div className="flex items-start gap-2">
        <ExclamationTriangleIconSolid className="text-palette-warning-icon mt-0.5 size-4" />
        <Typography variant="body-small" className="ndl-form-message">
          This will reset the deployment to unmonitored and allow a new Neo4j cluster to claim the deployment.
        </Typography>
      </div>
    </div>
  );
};

export const RegenerateTokenModal = ({
  isOpen,
  closeModal,
  deploymentId,
}: {
  isOpen: boolean;
  closeModal: () => void;
  deploymentId: string;
}) => {
  const activeProjectSummary = useActiveProjectSummary();
  const [regenerateToken, { error, isLoading }] = fleetManagementApi.useRegenerateTokenMutation();
  const [releaseToken] = fleetManagementApi.useReleaseTokenMutation();
  const [token, setToken] = useState<string>();
  const [autoRotate, setAutoRotate] = useState(true);
  const [expiry, setSelectedExpiry] = useState<TokenExpiryOption | undefined>();
  const [release, setRelease] = useState(false);

  const onClose = () => {
    setToken(undefined);
    closeModal();
  };

  return (
    <Dialog size="small" isOpen={isOpen} onClose={onClose}>
      <Dialog.Header>{isNotNullish(token) ? 'Token regenerated' : 'Regenerate token'}</Dialog.Header>
      <Dialog.Content className="flex flex-col gap-7">
        {error && <ApiErrorBanner error={error} />}
        {isNotNullish(token) && isNotNullish(expiry) ? (
          <TextInput isReadOnly={true} value={expiry.label} label="Token expiry" />
        ) : (
          <div className="flex flex-col gap-2">
            <TokenExpirySelection expiry={expiry} setSelectedExpiry={setSelectedExpiry} />
            <TokenAutoRotationOption autoRotate={autoRotate} setAutoRotate={setAutoRotate} />
            <TokenReleaseOption release={release} setRelease={setRelease} />
          </div>
        )}
        {isNotNullish(token) && <TokenOutput token={token} />}
      </Dialog.Content>
      <Dialog.Actions>
        <Button fill="outlined" color="neutral" onClick={onClose}>
          {isNotNullish(token) ? 'Done' : 'Cancel'}
        </Button>
        {isNullish(token) && (
          <Button
            fill="filled"
            color="primary"
            onClick={() => {
              void regenerateToken({
                projectId: activeProjectSummary?.id ?? '',
                req: {
                  new_rotation_interval: expiry?.label ?? '',
                  auto_rotate: autoRotate,
                },
                deploymentId,
              })
                .then((res) => {
                  if (isNotNullish(res.data) && isNonEmptyString(res.data.token)) {
                    setToken(res.data.token);
                    if (release) {
                      releaseToken({ projectId: activeProjectSummary?.id ?? '', deploymentId })
                        .then((tokenRes) => {
                          if (!tokenRes.error) {
                            toast.success(`Token regenerated and released`, { shouldAutoClose: true });
                          } else {
                            toast.danger(<ApiErrorBanner error={tokenRes.error} />, { shouldAutoClose: true });
                          }
                        })
                        .catch((err: Error) => {
                          toast.danger(err.message, { shouldAutoClose: true });
                        });
                    } else {
                      toast.success('Token regenerated', { shouldAutoClose: true });
                    }
                  }
                })
                .catch((err: Error) => {
                  toast.danger(err.message, { shouldAutoClose: true });
                });
            }}
            isLoading={isLoading}
            isDisabled={isNullish(expiry)}
          >
            Regenerate
          </Button>
        )}
      </Dialog.Actions>
    </Dialog>
  );
};
